import type { Dispatch, FC, ReactNode } from 'react';
import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import Dates from '../../shared/helpers/Dates';
import useToggleTranslations from '../../shared/hooks/useToggleTranslations';
import { SHORTCUTS, LOCALES } from '../constants';
import Languages from '../helpers/Languages';
import Shortcuts from '../helpers/Shortcuts';
import Storage from '../helpers/Storage';
import Validator from '../helpers/Validator';

type LocaleContextType = [string, Dispatch<string>];

const LocaleContext = createContext<LocaleContextType>(['en', () => 'en']);

const useLocale = () => useContext(LocaleContext);

interface LocaleProviderProps {
  children: ReactNode;
}

const LocaleProvider: FC<LocaleProviderProps> = ({ children }) => {
  const isInit = useRef(false);
  const [locale, setLocale] = useReducer(
    (state: string, newState: string): string => {
      const locale = newState?.toLowerCase();

      if (LOCALES.includes(locale)) {
        Storage.set(SHORTCUTS.LANGUAGE, locale);

        return locale;
      }

      return state;
    },
    Storage.get(
      SHORTCUTS.LANGUAGE,
      Shortcuts.get(SHORTCUTS.LANGUAGE, Languages.getCurrent()),
    ),
  );

  // If language was different than the one in html tag, it was never updated. This address that issue.
  useEffect(() => {
    if (isInit.current) {
      return;
    }

    isInit.current = true;

    if (Languages.getCurrent() !== locale) {
      Languages.setCurrent(locale);
    }
  }, [locale]);

  useToggleTranslations(locale, setLocale);
  Dates.locale(locale);
  Validator.locale(locale);

  return (
    <LocaleContext.Provider value={[locale, setLocale]}>
      {children}
    </LocaleContext.Provider>
  );
};

export { LocaleContext, LocaleProvider, useLocale };
